import { EnvironmentProviders, Provider, inject } from '@angular/core';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { APP_CONFIG } from '@core/config/app-config.token';
import { SessionExpiredHandler } from '@core/auth/session-expired.handler';
import { SKIP_SESSION_EXPIRED } from './graphql-context';

/**
 * Ends the session on a GraphQL `UNAUTHENTICATED` (sent as HTTP 200 with an `errors` array, so
 * HTTP interceptors never see it). Operations flagged with `SKIP_SESSION_EXPIRED` (refresh,
 * logout) handle that code themselves, which also keeps a failed refresh from looping.
 */
export function createSessionErrorLink(sessionExpired: SessionExpiredHandler): ApolloLink {
  return new ErrorLink(({ error, operation }) => {
    if (operation.getContext()[SKIP_SESSION_EXPIRED] === true) {
      return;
    }
    const unauthenticated =
      CombinedGraphQLErrors.is(error) &&
      error.errors.some((e) => e.extensions?.['code'] === 'UNAUTHENTICATED');
    if (unauthenticated) {
      sessionExpired.handle();
    }
  });
}

/**
 * Apollo client. Auth headers are added by the HttpClient interceptor; this link chain only
 * handles GraphQL-level auth errors.
 */
export function provideGraphql(): Provider | EnvironmentProviders {
  return provideApollo(() => {
    const config = inject(APP_CONFIG);
    const httpLink = inject(HttpLink);
    const sessionExpired = inject(SessionExpiredHandler);

    return {
      link: ApolloLink.from([
        createSessionErrorLink(sessionExpired),
        httpLink.create({ uri: config.graphqlUrl }),
      ]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: { fetchPolicy: 'cache-and-network' },
        query: { fetchPolicy: 'network-only' },
      },
    };
  });
}
